import { Injectable } from '@angular/core';
import { IpInfoIOI } from '@interfaces/ipinfo-io.interface';
import { JsonIPI } from '@interfaces/jsonip.interface';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { DeviceEnum } from '@enums/device.enum';
import { DeviceUUID } from 'device-uuid';

const BROWSER_ID_STORAGE_KEY = 'omni-browser-id';

@Injectable({
  providedIn: 'root',
})
export class ClientBrowserHelperService {
  private IpInfoUrl: string = environment.IPINFO_API;
  private jsonIp: string = environment.JSONIP_API;
  private geoIp: string = environment.GEOIP_API;
  private Browser: any = window.navigator;

  constructor(private http: HttpClient) {}

  public getIpAddress(retry = false): Observable<IpInfoIOI | JsonIPI> {
    if (!retry) return this.http.get<JsonIPI>(this.jsonIp);

    return this.http.get<IpInfoIOI>(this.IpInfoUrl);
  }

  public getLocationByIp(ip: string) {
    return this.http.get(`${this.geoIp}/${ip}`);
  }

  public getDevice() {
    return this.deviceType();
  }

  public getBrowser() {
    return this.browserDetect();
  }
  public getBrowserId(): string {
    const stored = this.readStoredBrowserId();
    if (stored) {
      return stored;
    }

    const browserId = this.randomBrowserId();
    if (!browserId || !this.storeBrowserId(browserId)) {
      return this.deviceFingerprint();
    }

    return browserId;
  }

  private readStoredBrowserId(): string | null {
    try {
      return localStorage.getItem(BROWSER_ID_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeBrowserId(browserId: string): boolean {
    try {
      localStorage.setItem(BROWSER_ID_STORAGE_KEY, browserId);
      return true;
    } catch {
      return false;
    }
  }

  private randomBrowserId(): string | null {
    const cryptoRef = window.crypto as Crypto & { randomUUID?: () => string };

    if (typeof cryptoRef?.randomUUID === 'function') {
      return cryptoRef.randomUUID();
    }

    if (typeof cryptoRef?.getRandomValues === 'function') {
      const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (byte) => ('0' + byte.toString(16)).slice(-2)).join('');
    }

    return null;
  }

  private deviceFingerprint(): string {
    const deviceUUID = new DeviceUUID().parse();
    const deviceInfos = [
      deviceUUID.language,
      deviceUUID.platform,
      deviceUUID.os,
      deviceUUID.version,
      deviceUUID.browser,
      deviceUUID.resolution,
      deviceUUID.cpuCores,
      deviceUUID.isAuthoritative,
      deviceUUID.silkAccelerated,
      deviceUUID.isDesktop,
      deviceUUID.isMobile,
      deviceUUID.isTablet,
      deviceUUID.isWindows,
      deviceUUID.isLinux,
      deviceUUID.isLinux64,
      deviceUUID.isMac,
      deviceUUID.isiPad,
      deviceUUID.isiPhone,
      deviceUUID.isiPod,
      deviceUUID.pixelDepth,
      deviceUUID.isTouchScreen,
    ];
    const uuid = deviceUUID.hashMD5(deviceInfos.join(':'));
    return uuid;
  }

  public getPlataform() {
    return this.osDetect();
  }

  private osDetect() {
    const browser = this.Browser.oscpu || this.Browser.platform;

    switch (true) {
      case browser.indexOf('Win') > -1:
        return 'Windows OS';
      case browser.indexOf('Mac') > -1 && !!(<any>window).opr:
        return 'MacOS';
      case browser.indexOf('X11') > -1 && !!(<any>window).chrome:
        return 'UNIX OS';
      case browser.indexOf('Linux') > -1:
        return 'Linux OS';
      default:
        return 'Other';
    }
  }

  private deviceType() {
    const ua = this.Browser.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return DeviceEnum.TABLET;
    } else if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
      return DeviceEnum.MOBILE;
    }
    return DeviceEnum.DESKTOP;
  }

  private browserDetect() {
    const agent = this.Browser.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'Edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'Opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'Chrome';
      case agent.indexOf('trident') > -1:
        return 'IE';
      case agent.indexOf('firefox') > -1:
        return 'Firefox';
      case agent.indexOf('safari') > -1:
        return 'Safari';
      default:
        return 'other';
    }
  }
}
